import { useEffect, useState } from "react";
import { Building2, Phone, Globe, Mail, MapPin } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn/dialog";
import { Spinner } from "@/components/common/Loading/Spinner";
import { fetchCompanyDetails } from "@/api/company/api";
import { useLayout } from "@/contexts/LayoutContext";
import type { CompanyDetails } from "@/types/domain";

type CompanyDetailsModalProps = {
  companyId: string;
  companyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DetailLine = ({ value }: { value: string }) => {
  if (!value) return null;
  return <span className="block">{value}</span>;
};

export const CompanyDetailsModal = ({ companyId, companyName, open, onOpenChange }: CompanyDetailsModalProps) => {
  const { data } = useLayout();
  const [details, setDetails] = useState<CompanyDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !companyId) return;

    setIsLoading(true);
    setError(null);
    setDetails(null);

    fetchCompanyDetails(companyId)
      .then(setDetails)
      .catch(() => setError("Failed to load company details."))
      .finally(() => setIsLoading(false));
  }, [open, companyId]);

  // Filter out empty strings — all fields are guaranteed strings from the API, but may be ""
  const addressLines = details
    ? [details.address1, details.address2, details.address3, details.address4, details.town, details.county, details.postcode, details.country].filter(Boolean)
    : [];

  const dashboardUrl = details ? `${data.appRoutes.dashboard}?customerid=${details.companyId}` : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={16} className="shrink-0 text-muted-foreground" />
            {details
              ? (
                <a
                  href={dashboardUrl!}
                  className="hover:underline text-blue-600"
                >
                  {details.accountNo} — {details.companyName}
                </a>
              )
              : companyName}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive py-2">{error}</p>
        )}

        {details && !isLoading && (
          <div className="space-y-3 text-sm">
            {addressLines.length > 0 && (
              <div className="flex gap-2">
                <MapPin size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                <address className="not-italic leading-snug">
                  {addressLines.map((line, i) => (
                    <DetailLine key={i} value={line} />
                  ))}
                </address>
              </div>
            )}

            {details.telephone && (
              <div className="flex gap-2 items-center">
                <Phone size={14} className="shrink-0 text-muted-foreground" />
                <a href={`tel:${details.telephone}`} className="hover:underline">
                  {details.telephone}
                </a>
              </div>
            )}

            {details.website && (
              <div className="flex gap-2 items-center">
                <Globe size={14} className="shrink-0 text-muted-foreground" />
                <a
                  href={details.webLink || details.website}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline truncate"
                >
                  {details.website}
                </a>
              </div>
            )}

            {details.email && (
              <div className="flex gap-2 items-center">
                <Mail size={14} className="shrink-0 text-muted-foreground" />
                <a href={`mailto:${details.email}`} className="hover:underline truncate">
                  {details.email}
                </a>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
